import { token } from '@atlaskit/tokens';
import FieldNameContainer from '@databases/field-name/FieldNameContainer';
import { FieldNameInlineContainer } from '@databases/field-name/FieldNameWithActions';
import { InlineEditContainer } from '@databases/inline-edit/InlineEdit';
import {
  BorderContainer,
  InlineEditWrapperContainer
} from '@databases/inline-edit/InlineEditWrapper';
import { useDatabaseSelectionSnapshot } from '@databases/ping-registry';
import { defaultStoreUtils } from '@databases/redux';
import { selectDatabaseDraftByContentId } from '@databases/redux/changesets/selectors';
import { DatabaseValueViewContainer, TABLE_BORDER_RADIUS } from '@databases/styles';
import { DatabaseLayoutType, shouldShowField } from '@databases/types/database/view';
import { TABLE_CLASSNAMES } from '@databases/utils/constants';
import {
  getOrderWithFallback,
  getViewSpecificOrderingExperimentValue
} from '@databases/utils/database/view/view';
import useView from '@databases/views/useView';
import React, {
  type FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import styled from 'styled-components';

import { getExperimentValue } from '@canvas/client-feature-flags';

import { gridTable, newTableBorderWidth, subtleBorder, SubtleTable } from '../styled.tsx';

import DatabaseEntryRow from './DatabaseEntryRow.tsx';

// Aligns field icons and editable names with the first line of their values.
const ENTRY_DETAILS_FIELD_NAME_OFFSET = '13px';

export const Container = styled.div<{
  isEmbedEntryView?: boolean;
  $presentationKind?: DatabaseEntryPresentation['kind'];
}>`
  min-height: 0;
  overflow: ${(props) => (props.isEmbedEntryView ? 'visible' : 'auto')};
  box-sizing: border-box;
  flex-grow: 1;
  margin-bottom: ${token('space.100', '8px')};

  ${(props) =>
    props.$presentationKind === 'native-embed-v2' &&
    `
    padding-bottom: ${token('space.200', '16px')};
    margin-bottom: 0;
  `}

  ${(props) =>
    props.$presentationKind === 'entry-details-redesign' &&
    `
    margin-bottom: 0;
    overflow-x: ${props.isEmbedEntryView ? 'visible' : 'clip'};
    overflow-y: visible;
  `}
`;

export const Table = styled(SubtleTable)<{
  fieldNameWidth?: number;
  $presentationKind?: DatabaseEntryPresentation['kind'];
}>`
  ${gridTable}
  grid-template-columns: ${(props) => `${props.fieldNameWidth ?? 260}px`} 1fr;
  overflow-wrap: anywhere;

  ${(props) =>
    props.$presentationKind === 'native-embed-v2' &&
    `
    &, > * > tr > *, > * > tr > *:last-child {
      border: none !important;
    }

    > * > tr:first-child > * {
      padding-top: ${token('space.050', '4px')};
    }

    > * > tr:last-child > * {
      padding-bottom: ${token('space.050', '4px')};
    }
  `}

  ${(props) =>
    props.$presentationKind === 'native-embed-v2' &&
    `
    grid-template-columns: ${props.fieldNameWidth ?? 140}px 1fr;
    column-gap: ${token('space.100', '8px')};

    > * > tr > * {
      min-height: 32px;
    }

    > * > tr > th ${FieldNameInlineContainer} > ${FieldNameContainer} > :first-child {
      top: 15px;
    }

    > * > tr > th ${FieldNameInlineContainer} > ${FieldNameContainer} > ${InlineEditWrapperContainer}:nth-child(2) {
      height: auto;
      justify-content: flex-start;
      top: 15px;
    }
  `}

  ${(props) =>
    props.$presentationKind === 'entry-details-redesign' &&
    `
    &, > * > tr > *, > * > tr > *:last-child {
      border: none !important;
    }

    grid-template-columns: clamp(120px, 40%, 156px) minmax(0, 1fr);
    column-gap: ${token('space.100', '8px')};
    row-gap: ${token('space.100', '8px')};
    width: 100%;
    max-width: 100%;
    min-width: 0;
    box-sizing: border-box;

    > * > tr > th {
      color: ${token('color.text.subtle')};
      font: ${token('font.heading.xxsmall')};
    }

    > * > tr > td > ${InlineEditWrapperContainer} {
      min-width: 0;
      max-width: 100%;

      ${InlineEditContainer} {
        border-radius: ${token('radius.medium', '6px')};
      }

      > ${BorderContainer} {
        margin: 0;
      }
    }

    > * > tr > td > ${InlineEditWrapperContainer}.${TABLE_CLASSNAMES.IS_SELECTED} {
      > ${BorderContainer} {
        display: none;
      }

      [data-inline-edit-read-view] > ${DatabaseValueViewContainer} {
        border-radius: ${token('radius.small', '3px')};
        box-shadow: inset 0 0 0 ${token('border.width.selected', '2px')}
          ${token('color.border.selected', '#0C66E4')};
        min-height: 42px;
      }
    }

    > * > tr > td > ${InlineEditWrapperContainer} ${DatabaseValueViewContainer}:has([data-databases-checkbox-value]) {
      display: flex;
      align-items: center;
      min-height: 42px;
      padding-block: 0;
    }

    > * > tr > th ${FieldNameInlineContainer} > ${FieldNameContainer} > :first-child {
      left: 0;
      top: ${ENTRY_DETAILS_FIELD_NAME_OFFSET};
    }

    > * > tr > th ${FieldNameInlineContainer} > ${FieldNameContainer} > ${InlineEditWrapperContainer}:nth-child(2) {
      height: auto;
      justify-content: flex-start;
      top: ${ENTRY_DETAILS_FIELD_NAME_OFFSET};
    }
  `}

  > * > tr > * {
    display: flex;
    flex-direction: column;
    min-height: 42px;
    min-width: 0;
    padding: 0;
    box-sizing: border-box;

    &:last-child {
      border-right: ${subtleBorder};
      border-width: ${newTableBorderWidth}px;
    }

    > ${InlineEditWrapperContainer} {
      flex-grow: 1;

      ${FieldNameInlineContainer} {
        box-sizing: border-box;
        padding: ${token('space.100', '8px')};
        font: ${token('font.heading.xxsmall')};
      }
    }
  }

  > * > tr {
    &:first-child {
      th {
        border-start-start-radius: ${TABLE_BORDER_RADIUS};
      }
      td {
        border-start-end-radius: ${TABLE_BORDER_RADIUS};
      }
    }
    &:last-child {
      th {
        border-end-start-radius: ${TABLE_BORDER_RADIUS};
      }
      td {
        border-end-end-radius: ${TABLE_BORDER_RADIUS};
      }
    }
  }
`;

export type DatabaseEntryPresentation =
  | { kind: 'default' }
  | { kind: 'native-embed-v2'; hideFirstVisibleField: boolean }
  | { kind: 'entry-details-redesign' };

const defaultPresentation: DatabaseEntryPresentation = { kind: 'default' };

const shouldHideFirstVisibleField = (presentation: DatabaseEntryPresentation) => {
  switch (presentation.kind) {
    case 'default':
      return false;
    case 'native-embed-v2':
      return presentation.hideFirstVisibleField;
    case 'entry-details-redesign':
      return false;
    default: {
      const exhaustivePresentation: never = presentation;
      return exhaustivePresentation;
    }
  }
};

export interface DatabaseEntryProps {
  contentId: string;
  entryId: string;
  isContentEditable?: boolean;
  fieldNameWidth?: number;
  /** If true, focus the first value. */
  autoFocus?: boolean;
  isEmbedEntryView?: boolean;
  /** If true, displays greyed out / color.disabled for database values */
  showDisabledValue?: boolean;
  presentation?: DatabaseEntryPresentation;
  emptyValueLabel?: string;
}

/**
 * Renders a table that shows all the field names and values of a database entry. The field names and values can be edited
 * using inline edit fields, and new fields can be added. Any edits will cause a database update to be drafted automatically.
 */
const DatabaseEntry: FunctionComponent<DatabaseEntryProps> = ({
  contentId,
  entryId,
  isContentEditable = false,
  fieldNameWidth,
  autoFocus,
  isEmbedEntryView,
  showDisabledValue = false,
  presentation = defaultPresentation,
  emptyValueLabel
}) => {
  const hideFirstVisibleField = shouldHideFirstVisibleField(presentation);
  const { view } = useView();
  const [databaseFieldIds, operations, isLocked] = defaultStoreUtils.useEqualSelector(
    (state) => {
      const databaseDraft = selectDatabaseDraftByContentId(state, {
        contentId,
        includeDeleted: true
      });
      return [
        databaseDraft.fields,
        databaseDraft.operations,
        databaseDraft.meta.isLocked
      ] as const;
    }
  );

  const useViewSpecificOrdering = getViewSpecificOrderingExperimentValue();
  const allFieldIds = useMemo(
    () =>
      useViewSpecificOrdering
        ? getOrderWithFallback(databaseFieldIds, view.settings.fieldOrder)
        : databaseFieldIds,
    [databaseFieldIds, useViewSpecificOrdering, view.settings.fieldOrder]
  );

  const fieldVisibilityMap = view.settings.fields;

  const isHideFieldsEnabled = useMemo(() => {
    const isMauiEnabled = getExperimentValue('mauiExperiment', 'isEnabled', {
      fireExperimentExposure: false
    });
    return (
      isMauiEnabled &&
      getExperimentValue('dbNativeEmbedsV2', 'isEnabled', {
        fireExperimentExposure: true
      })
    );
  }, []);

  const isTimelineLayoutEnabled = useMemo(
    () =>
      getExperimentValue('databasesRoadmap', 'isEnabled', {
        fireExperimentExposure: false
      }) === true,
    []
  );

  const shouldFilterHiddenFields =
    isHideFieldsEnabled &&
    (presentation.kind !== 'entry-details-redesign' || isEmbedEntryView) &&
    (!isTimelineLayoutEnabled ||
      view.settings.layout.type !== DatabaseLayoutType.TIMELINE);

  const fieldIds = useMemo(() => {
    if (!shouldFilterHiddenFields) {
      return hideFirstVisibleField ? allFieldIds.slice(1) : allFieldIds;
    }
    const visibleFieldIds = allFieldIds.filter((fieldId) =>
      shouldShowField(fieldVisibilityMap, fieldId)
    );
    return hideFirstVisibleField ? visibleFieldIds.slice(1) : visibleFieldIds;
  }, [allFieldIds, fieldVisibilityMap, hideFirstVisibleField, shouldFilterHiddenFields]);

  const canEditContent = isContentEditable && operations.updateEntries;
  const canEditFields =
    isContentEditable && operations.updateMetadata && !isLocked && !isEmbedEntryView;
  const showEntryDetailsFieldActions =
    presentation.kind === 'entry-details-redesign' && canEditFields;

  const editingIds = useRef(new Set<string>());

  const [selectedPanelFieldId, setSelectedPanelFieldId] = useState<string | null>(null);

  const selectPanelField = useCallback((nextFieldId: string) => {
    setSelectedPanelFieldId(nextFieldId);
  }, []);

  const clearPanelSelection = useCallback(() => setSelectedPanelFieldId(null), []);

  // Keep panel and grid selections mutually exclusive.
  const hasGlobalSelection = useDatabaseSelectionSnapshot((reader) =>
    reader.hasSelection()
  );
  useEffect(() => {
    if (hasGlobalSelection) {
      // oxlint-disable-next-line react/set-state-in-effect -- Global selection invalidates transient panel selection.
      setSelectedPanelFieldId(null);
    }
  }, [hasGlobalSelection]);

  return (
    <Container isEmbedEntryView={isEmbedEntryView} $presentationKind={presentation.kind}>
      <Table
        data-testid={'database-entry-table'}
        fieldNameWidth={fieldNameWidth}
        $presentationKind={presentation.kind}
      >
        <tbody>
          {fieldIds.map((fieldId, i) => (
            <DatabaseEntryRow
              key={fieldId}
              contentId={contentId}
              entryId={entryId}
              fieldId={fieldId}
              isContentEditable={isContentEditable}
              canEditContent={canEditContent}
              canEditFields={canEditFields}
              editingIds={editingIds}
              // oxlint-disable-next-line jsx-a11y/no-autofocus -- Opening an entry for editing must transfer focus to its first field.
              autoFocus={autoFocus && i === 0}
              showDisabledValue={showDisabledValue}
              selectedPanelFieldId={selectedPanelFieldId}
              selectPanelField={selectPanelField}
              clearPanelSelection={clearPanelSelection}
              emptyValueLabel={emptyValueLabel}
              showEntryDetailsFieldActions={showEntryDetailsFieldActions}
            />
          ))}
        </tbody>
      </Table>
    </Container>
  );
};

export default DatabaseEntry;
